import { getAppConfigValue } from "@elliemae/pui-app-sdk";
import { initLogRocket } from "@elliemae/pui-logrocket";
import { randomUUID } from "@frontend/utils";
const getLogRocketInstance = () => window.emui?.logRocket;
function initializeLogRocket(config) {
  const appId = config?.appId ?? getAppConfigValue("logRocketConfig.appId");
  const parentDomain = config?.parentDomain ?? getAppConfigValue("logRocketConfig.parentDomain");
  const rootHostname = config?.rootHostname ?? getAppConfigValue("logRocketConfig.rootHostname");
  const childDomains = config?.childDomains ?? getAppConfigValue("logRocketConfig.childDomains");
  window.process = window.process || { env: {} };
  initLogRocket({
    appId,
    parentDomain,
    rootHostname,
    childDomains,
    network: {
      requestSanitizer: sanitizeRequest,
      responseSanitizer: sanitizeResponse
    }
  });
  setTimeout(() => {
    getLogRocketInstance()?.startNewSession?.();
  }, 0);
  installCorrelationIdInterceptor();
}
function identifyLogRocketUser(traits, userId) {
  const id = userId ?? randomUUID();
  getLogRocketInstance()?.identify(id, traits);
}
const SENSITIVE_HEADERS = [
  "authorization",
  "cookie",
  "set-cookie",
  "x-opaque-token",
  "x-elli-pat"
];
const SENSITIVE_BODY_KEYS = [
  // borrower names
  "firstName",
  "lastName",
  "firstNameWithMiddleName",
  "lastNameWithSuffix",
  // contact
  "emailAddressText",
  "workEmailAddress",
  "email",
  "homePhoneNumber",
  // address
  "mailingAddress",
  "addressCity",
  "addressState",
  "addressPostalCode",
  "addressStreetLine1",
  "streetAddress",
  "streetAddressLine1",
  "streetAddressLine2",
  "subjectPropertyStreetAddress",
  "subjectPropertyPropertyStreetAddress",
  "urla2020StreetAddress",
  // credit / financial
  "creditScore",
  "creditScoreToUse",
  "ausAverageMedianCreditScore",
  "totalMonthlyIncome",
  "totalMonthlyIncomeMinusNetRentalAmount",
  "assets",
  "liabilities",
  "assetsAvailableAmount",
  // HMDA object - contains race / ethnicity / sex
  "hmda"
];
function redactHeaders(headers) {
  if (!headers) return;
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      headers[key] = "";
    }
  }
}
function redactBodyKeys(value) {
  if (Array.isArray(value)) return value.map(redactBodyKeys);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = SENSITIVE_BODY_KEYS.includes(k) ? "[REDACTED]" : redactBodyKeys(v);
    }
    return out;
  }
  return value;
}
function sanitizeBody(body) {
  if (!body) return body;
  try {
    return JSON.stringify(redactBodyKeys(JSON.parse(body)));
  } catch {
    return null;
  }
}
function sanitizeRequest(request) {
  redactHeaders(request.headers);
  request.body = sanitizeBody(request.body);
  return request;
}
function sanitizeResponse(response) {
  if (response.headers?.["x-secret"]) return null;
  redactHeaders(response.headers);
  response.body = sanitizeBody(response.body);
  return response;
}
const CORRELATION_ID_HEADER = "X-Correlation-Id";
function installCorrelationIdInterceptor() {
  if (typeof window === "undefined" || !window.fetch) return;
  if (window.__logRocketCorrelationPatched) return;
  window.__logRocketCorrelationPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    try {
      const correlationId = response.headers.get(CORRELATION_ID_HEADER);
      if (correlationId) {
        const method = (input instanceof Request ? input.method : init?.method) ?? "GET";
        getLogRocketInstance()?.track("otel-correlation", {
          correlationId,
          method: method.toUpperCase(),
          url: response.url,
          status: response.status
        });
      }
    } catch {
    }
    return response;
  };
}
export {
  identifyLogRocketUser,
  initializeLogRocket
};
